import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, Rocket } from "lucide-react";
import { ART } from "@/lib/artwork";

const JOURNEY = "/journey";

/**
 * JourneyCTA — in-content lead capture. Three visual weights so the same
 * offer can repeat down a long page without feeling repetitive.
 *  bar   → slim inline nudge
 *  card  → mid-content framed invite
 *  block → full-width closing block on artwork
 */
export function JourneyCTA({ variant = "card", index = 0 }) {
  const { t } = useTranslation();
  const title = t("journeyCta.title", { defaultValue: "Start your journey" });
  const body = t("journeyCta.body", {
    defaultValue: "Tell us about your business and get a tailored plan, mentors and a data room in minutes.",
  });
  const action = t("journeyCta.action", { defaultValue: "Start your journey" });
  const testId = `journey-cta-${variant}-${index}`;

  if (variant === "bar") {
    return (
      <Link
        to={JOURNEY}
        className="group my-10 flex flex-wrap items-center gap-3 rounded-2xl border border-primary/25 bg-primary/[0.06] px-4 py-3.5 no-underline transition-colors hover:border-primary/50 hover:bg-primary/[0.1]"
        data-testid={testId}
      >
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">{title}</span>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          {action}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl-flip" />
        </span>
      </Link>
    );
  }

  if (variant === "block") {
    return (
      <div
        className="relative my-14 overflow-hidden rounded-3xl border border-border"
        data-testid={testId}
      >
        <img src={ART.cta} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0d1b1a]/72" aria-hidden />
        <div className="relative px-6 py-12 sm:px-10 sm:py-14 text-white">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/70">The Saudi Gate</span>
          <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight max-w-xl">{title}</h3>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-white/80 leading-relaxed">{body}</p>
          <Link
            to={JOURNEY}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-primary)] px-6 py-3 text-sm font-semibold text-white no-underline transition-transform hover:scale-[1.02]"
            data-testid={`${testId}-link`}
          >
            <Rocket className="h-4 w-4" />
            {action}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="my-12 rounded-2xl border border-border bg-card/80 p-5 sm:p-6 backdrop-blur"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-base md:text-lg font-semibold leading-tight">{title}</h3>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
          <Link
            to={JOURNEY}
            className="mt-3.5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary no-underline hover:underline"
            data-testid={`${testId}-link`}
          >
            {action}
            <ArrowRight className="h-3.5 w-3.5 rtl-flip" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default JourneyCTA;
